import { countsTowardActionNeeded, markAskClosed, unmarkAskClosed, type OpenAsksRead, type WebOpenAsk } from './open-asks';

// Acting on an open ask without opening its conversation: "Not for me" closes it, and a reply typed on the row
// answers it. Both are optimistic — the row leaves the cache at once and is remembered as closed for this page
// session — through the one cache the Action needed tab and the island read (`queryKeys.openAsks`).
//
// The server half: "Not for me" is the client signal ASK_DISMISSED, which resolves the ask's ledger row; a reply
// is an ordinary message in the ask's conversation, and the ledger resolves that conversation's ask when the reply
// lands (USER_REPLIED names the chat). So a reply needs no signal of its own here.
//
// Pure, no runtime imports — the cache and `fetch` are handed in — the same shape as `chase-item.ts`'s close.

/** The ledger asks the row can close: a chat's ask, a card, a held send. A Decision is answered by its chips, a
 *  chase item by its own two actions. */
const CLOSABLE_PREFIXES = ['conv:', 'card:', 'held-send:'];

export function isClosableAsk(ask: Pick<WebOpenAsk, 'id' | 'prompt' | 'chase'>): boolean {
  return !ask.prompt && !ask.chase && CLOSABLE_PREFIXES.some((prefix) => ask.id.startsWith(prefix));
}

/** A reply can be typed only into a chat's own ask — the one the reply resolves. */
export function isReplyableAsk(ask: Pick<WebOpenAsk, 'id' | 'prompt' | 'chase'>): boolean {
  return isClosableAsk(ask) && ask.id.startsWith('conv:');
}

export function withoutAsk(read: OpenAsksRead | null | undefined, id: string): OpenAsksRead | null | undefined {
  if (!read) return read;
  const removed = read.asks.find((ask) => ask.id === id);
  if (!removed) return read;
  const asks = read.asks.filter((ask) => ask !== removed);
  if (read.total === undefined) return { asks };
  return { asks, total: Math.max(0, read.total - (countsTowardActionNeeded(removed) ? 1 : 0)) };
}

/** A failed close puts back only that row, into the cache as it is now, newest first among the non-chase asks. */
export function withAskRestored(read: OpenAsksRead | null | undefined, ask: WebOpenAsk): OpenAsksRead | null | undefined {
  if (!read || read.asks.some((a) => a.id === ask.id)) return read;
  const time = (a: WebOpenAsk) => (a.createdDate ? Date.parse(a.createdDate) || 0 : 0);
  const others = [...read.asks.filter((a) => !a.chase), ask].sort((a, b) => time(b) - time(a));
  const asks = [...others, ...read.asks.filter((a) => !!a.chase)];
  if (read.total === undefined) return { asks };
  return { asks, total: read.total + (countsTowardActionNeeded(ask) ? 1 : 0) };
}

export interface AskCloseCache {
  updateOpenAsks(update: (current: OpenAsksRead | null | undefined) => OpenAsksRead | null | undefined): void;
  invalidateTasks(): void;
}

export type AskSignalPost = (body: Record<string, string>) => Promise<{ ok: boolean }>;

/** "Not for me". Resolves `true` when the server took it; a refused or lost one puts the row back. */
export async function dismissAsk(ask: WebOpenAsk, cache: AskCloseCache, post: AskSignalPost): Promise<boolean> {
  markAskClosed(ask.id);
  cache.updateOpenAsks((current) => withoutAsk(current, ask.id));
  try {
    const res = await post({ kind: 'askDismissed', askKey: ask.id });
    if (!res.ok) throw new Error('client-signal refused the dismiss');
  } catch {
    unmarkAskClosed(ask.id);
    cache.updateOpenAsks((current) => withAskRestored(current, ask));
    return false;
  }
  cache.invalidateTasks();
  return true;
}

/** A reply was sent into the ask's conversation: the row leaves now; the ledger resolves it when the reply lands. */
export function markAskAnswered(ask: WebOpenAsk, cache: AskCloseCache): void {
  markAskClosed(ask.id);
  cache.updateOpenAsks((current) => withoutAsk(current, ask.id));
}
